// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Skill Calculator
//
// Calculates the skill_id for a given questions
/* eslint-disable class-methods-use-this */

import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import * as Skills from './skills';
import Note from '../uTheory.Notation/note';
import type Clef from '../uTheory.Notation/clef';
import type Interval from '../uTheory.Notation/interval';
import type KeySignature from '../uTheory.Notation/keySignature';
import type Chord from '../uTheory.Notation/chord';

const makeInt = Skills.makeInt;

export default class SkillsCalculator {
  // Public constructor

  getPianoSkill(noteOrNoteName: NoteName | Note): number {
    const note = new Note(noteOrNoteName);
    switch (note.accidental) {
      case '#':
        return makeInt('sharps') as number;
      case 'b':
        return makeInt('flats') as number;
      case 'x':
        return makeInt('double_sharps') as number;
      case 'bb':
        return makeInt('double_flats') as number;
      case '':
        if (note.letter === 'A' || note.letter === 'B') {
          return makeInt('piano_a_b') as number;
        }
        return makeInt('piano_c_g') as number;
    }
    throw new Error('NaN: Unable to calculate piano skill.  Invalid note.');
  }

  getClefSkill(note: NoteName | Note, clef: Clef): number {
    const line = clef.getLine(note);

    // Treble Skills:
    if (clef.getName() === 'treble') {
      if (line > 5) {
        return makeInt('treble_high_legers') as number;
      }
      if (line < 1) {
        return makeInt('treble_low_legers') as number;
      }
      if (line > 2) {
        return makeInt('treble_a_g') as number;
      }
      return makeInt('treble_c_g') as number;
    }

    // Bass Skills:
    if (clef.getName() === 'bass') {
      if (line > 5) return makeInt('bass_high_legers') as number;
      if (line < 1) return makeInt('bass_low_legers') as number;
      if ((line * 2) % 2 === 0) return makeInt('bass_lines') as number;
      return makeInt('bass_spaces') as number;
    }

    const skill = makeInt(`${clef.getName().replace('-', '_')}_clef`);
    if (typeof skill !== 'undefined') return skill;

    throw new Error('NaC: Unable to determine clef skill.  Invalid note or clef.');
  }

  getPianoHalfWholeSkill(interval: Interval): number | false {
    // eslint-disable-next-line eqeqeq
    if (interval.genericInterval == 2) {
      switch (interval.quality) {
        case 'M':
          return makeInt('whole_steps_piano') as number;
        case 'm':
          return makeInt('half_steps_piano') as number;
        case 'A':
          return makeInt('augmented_seconds_piano') as number;
        default:
          // augmented, diminished, etc...
          return false;
      }
    }
    // eslint-disable-next-line eqeqeq
    if (interval.genericInterval == 1 && interval.quality === 'A') {
      return makeInt('half_steps_piano') as number;
    }
    return false;
  }

  getStaffHalfWholeSkill(interval: Interval): number | false {
    if (interval.genericInterval === 2) {
      switch (interval.quality) {
        case 'M':
          return makeInt('ws') as number;
        case 'm':
          return makeInt('dhs') as number;
        case 'A':
          return makeInt('A2') as number;
      }
    }
    if (interval.genericInterval === 1 && interval.quality === 'A') {
      return makeInt('chs') as number;
    }
    return false;
  }

  getPianoScaleSkill(mode: string): string {
    switch (mode) {
      case 'Major':
        return 'major_scales_piano';
      case 'naturalMinor':
        return 'natural_minor_piano';
      case 'harmonicMinor':
        return 'harmonic_minor_piano';
      case 'melodicMinorAscending':
      case 'melodicMinorDescending':
        return 'melodic_minor_piano';
    }
    throw new Error(`NaM: ${mode} is not a valid mode name.`);
  }

  getStaffScaleSkill(mode: string): string {
    switch (mode) {
      case 'Major':
        return 'major_scales_staff';
      case 'naturalMinor':
        return 'natural_minor';
      case 'harmonicMinor':
        return 'harmonic_minor';
      case 'melodicMinorAscending':
      case 'melodicMinorDescending':
        return 'melodic_minor';
    }
    throw new Error(`NaM: ${mode} is not a valid mode name.`);
  }

  getKeySignatureSkill(key: KeySignature, includeETIdentifyTonicSkills = false): string[] {
    if (includeETIdentifyTonicSkills) {
      return [...this.getKeySignatureTheorySkill(key), ...this.getETIdentifyTonicSkills(key)];
    }
    return this.getKeySignatureTheorySkill(key);
  }

  private getKeySignatureTheorySkill(key: KeySignature) {
    if (key.accidentalsCount === 0) {
      if (key.quality === 'Major') {
        return ['c_major'];
      }
      return ['a_minor'];
    }
    if (key.accidentalsCount > 0) {
      if (key.quality === 'Major') {
        return ['sharp_majors'];
      }
      return ['sharp_minors'];
    }

    if (key.quality === 'Major') {
      return ['flat_majors'];
    }
    return ['flat_minors'];
  }

  private getETIdentifyTonicSkills(key: KeySignature) {
    if (key.accidentalsCount === 0) {
      if (key.quality === 'Major') {
        return ['identify_tonic_c_major'];
      }
      return ['identify_tonic_a_minor'];
    }
    if (key.accidentalsCount > 0) {
      if (key.quality === 'Major') {
        return ['identify_tonic_major_sharp_keys'];
      }
      return ['identify_tonic_minor_sharp_keys'];
    }

    if (key.quality === 'Major') {
      return ['identify_tonic_major_flat_keys'];
    }
    return ['identify_tonic_minor_flat_keys'];
  }

  getParallelOrRelativeKeysSkill(parallelOrRelative: 'parallel' | 'relative'): string {
    if (parallelOrRelative === 'parallel') {
      return 'parallel_keys';
    }
    if (parallelOrRelative === 'relative') {
      return 'relative_keys';
    }
    throw new Error('parallelOrRelative must be parallel or relative');
  }

  getChordSkill(chord: Chord): string[] {
    const seventh = chord.isSeventh;
    const inversion = chord.inversion;
    const quality = chord.quality;
    const skills: string[] = [];

    if (seventh === true) {
      // var skill = (inversion === 0) ? 'seventh_chords' : 'seventh_chord_inversions';
      switch (quality) {
        case 'M7':
          skills.push('major_seventh_chords');
          break;
        case 'm7':
          skills.push('minor_seventh_chords');
          break;
        case '7':
          skills.push('dominant_seventh_chords');
          break;
        case 'o/7':
          skills.push('half_diminished_seventh_chords');
          break;
        case 'o7':
          skills.push('diminished_seventh_chords');
          break;
        default:
          return [];
      }

      if (inversion > 0) {
        skills.push('seventh_chord_inversion');
      }
    } else {
      switch (quality) {
        case 'M':
          skills.push('major_triads');
          break;
        case 'm':
          skills.push('minor_triads');
          break;
        case 'A':
          skills.push('augmented_triads');
          break;
        case 'o':
          skills.push('diminished_triads');
          break;
        default:
          return [];
      }

      if (inversion > 0) {
        skills.push('triad_inversion');
      }
    }

    return skills;
  }

  getPianoIntervalSkill(interval: Interval): string[] {
    const skills: string[] = [];
    const quality = interval.quality;
    const genericInterval = interval.genericInterval;
    const direction = interval.direction;

    return direction === -1 ? getDescendingSkills() : getAscendingSkills();

    function getDescendingSkills() {
      switch (quality) {
        case 'm':
        case 'P':
          if (genericInterval === 2) {
            skills.push('half_steps_piano');
          }
          skills.push('descending_minor_perfect');
          break;
        case 'M':
          if (genericInterval === 2) {
            skills.push('whole_steps_piano');
          }
          skills.push('descending_others');
          break;
        case 'd':
        case 'dd':
        case 'A':
        case 'AA':
          skills.push('descending_others');
          break;
      }

      return skills;
    }

    function getAscendingSkills() {
      switch (quality) {
        case 'M':
        case 'P':
          if (genericInterval === 2) {
            skills.push('whole_steps_piano');
          }
          skills.push('ascending_major_perfect');
          break;
        case 'm':
          if (genericInterval === 2) {
            skills.push('half_steps_piano');
          }
          skills.push('ascending_others');
          break;
        case 'd':
        case 'dd':
        case 'A':
        case 'AA':
          skills.push('ascending_others');
          break;
      }

      return skills;
    }
  }

  getStaffIntervalSkills(interval: Interval): string[] {
    const skills: string[] = [];
    const quality = interval.quality;
    const genericInterval = interval.genericInterval;
    const direction = interval.direction;

    if (direction === -1) getDescendingSkills();
    else getAscendingSkills();

    if (genericInterval > 8) skills.push('compound_intervals');
    return skills;

    function getDescendingSkills() {
      switch (quality) {
        case 'm':
        case 'P':
          if (genericInterval === 2) {
            skills.push('dhs');
          }
          skills.push('descending_minor_perfect');
          break;
        case 'M':
        case 'd':
        case 'dd':
        case 'A':
        case 'AA':
          if (genericInterval === 1) {
            skills.push('chs');
          } else if (genericInterval === 2) {
            skills.push('ws');
          }
          skills.push('descending_others');
          break;
      }

      return skills;
    }

    function getAscendingSkills() {
      switch (quality) {
        case 'P':
        case 'M':
          if (genericInterval === 2) {
            skills.push('ws');
          }
          skills.push('ascending_major_perfect');
          break;
        case 'm':
        case 'd':
        case 'dd':
        case 'A':
        case 'AA':
          if (genericInterval === 1) {
            skills.push('chs');
          } else if (genericInterval === 2) {
            skills.push('dhs');
          }
          skills.push('ascending_others');
          break;
      }

      return skills;
    }
  }
}
